export const COOKIE_CONSENT_OPEN_EVENT = "innovance:open-cookie-preferences";
export const COOKIE_CONSENT_CHANGED_EVENT =
  "innovance:cookie-preferences-changed";
export const COOKIE_CONSENT_STORAGE_KEY = "innovance-cookie-consent";

const CONSENT_VERSION = 1;
const CONSENT_DURATION_MS = 183 * 24 * 60 * 60 * 1000;
const GOOGLE_ANALYTICS_ID = "G-0SR1KDXG9F";
const LINKEDIN_PARTNER_ID = "8866129";
let volatileConsent: CookieConsentRecord | null = null;

export interface CookieConsentSelection {
  analytics: boolean;
  marketing: boolean;
  externalMedia: boolean;
}

export interface CookieConsentRecord extends CookieConsentSelection {
  version: number;
  decidedAt: number;
  expiresAt: number;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    lintrk?: ((...args: unknown[]) => void) & { q: unknown[][] };
    _linkedin_data_partner_ids?: string[];
  }
}

const isBrowser = () => typeof window !== "undefined";

export const getCookieConsent = (): CookieConsentRecord | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const storedValue = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!storedValue) {
      return volatileConsent;
    }

    const consent = JSON.parse(storedValue) as CookieConsentRecord;
    if (
      consent.version !== CONSENT_VERSION ||
      typeof consent.analytics !== "boolean" ||
      typeof consent.marketing !== "boolean" ||
      typeof consent.externalMedia !== "boolean" ||
      typeof consent.decidedAt !== "number" ||
      !isFinite(consent.decidedAt) ||
      typeof consent.expiresAt !== "number" ||
      !isFinite(consent.expiresAt) ||
      consent.decidedAt > consent.expiresAt ||
      consent.expiresAt <= Date.now()
    ) {
      window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
      volatileConsent = null;
      removeTrackingCookies();
      return null;
    }

    volatileConsent = consent;
    return consent;
  } catch {
    try {
      window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    } catch {
      // Storage may be unavailable in hardened browser configurations.
    }
    return volatileConsent;
  }
};

export const saveCookieConsent = (
  selection: CookieConsentSelection
): CookieConsentRecord => {
  const now = Date.now();
  const consent: CookieConsentRecord = {
    ...selection,
    version: CONSENT_VERSION,
    decidedAt: now,
    expiresAt: now + CONSENT_DURATION_MS,
  };

  if (isBrowser()) {
    volatileConsent = consent;
    try {
      window.localStorage.setItem(
        COOKIE_CONSENT_STORAGE_KEY,
        JSON.stringify(consent)
      );
    } catch {
      // The choice remains valid for this page session and trackers stay
      // disabled again after a reload when storage is unavailable.
    }
    window.dispatchEvent(
      new CustomEvent<CookieConsentRecord>(COOKIE_CONSENT_CHANGED_EVENT, {
        detail: consent,
      })
    );
  }

  return consent;
};

export const openCookiePreferences = (): void => {
  if (isBrowser()) {
    window.dispatchEvent(new Event(COOKIE_CONSENT_OPEN_EVENT));
  }
};

const appendScript = (id: string, src: string): HTMLScriptElement | null => {
  if (!isBrowser() || document.getElementById(id)) {
    return null;
  }

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
  return script;
};

export const trackGooglePageView = (path?: string): void => {
  if (!isBrowser() || !getCookieConsent()?.analytics || !window.gtag) {
    return;
  }

  window.gtag("event", "page_view", {
    page_path: path || window.location.pathname + window.location.search,
    page_location: window.location.href,
    page_title: document.title,
  });
};

const loadGoogleAnalytics = (): void => {
  if (!isBrowser() || document.getElementById("innovance-google-analytics")) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function (...args: unknown[]) {
      window.dataLayer?.push(args);
    };

  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("js", new Date());
  window.gtag("config", GOOGLE_ANALYTICS_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  appendScript(
    "innovance-google-analytics",
    `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`
  );
  trackGooglePageView();
};

const loadLinkedInInsight = (): void => {
  if (!isBrowser()) {
    return;
  }

  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  if (!window._linkedin_data_partner_ids.includes(LINKEDIN_PARTNER_ID)) {
    window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  }

  if (!window.lintrk) {
    const lintrk = ((...args: unknown[]) => {
      lintrk.q.push(args);
    }) as NonNullable<Window["lintrk"]>;
    lintrk.q = [];
    window.lintrk = lintrk;
  }

  appendScript(
    "innovance-linkedin-insight",
    "https://snap.licdn.com/li.lms-analytics/insight.min.js"
  );
};

export const enableConsentedServices = (
  consent: CookieConsentSelection
): void => {
  if (consent.analytics) {
    loadGoogleAnalytics();
  }
  if (consent.marketing) {
    loadLinkedInInsight();
  }
};

const expireCookie = (cookieName: string): void => {
  const domains = [undefined, window.location.hostname];
  const hostParts = window.location.hostname.split(".");
  if (hostParts.length > 2) {
    domains.push(`.${hostParts.slice(-2).join(".")}`);
  }

  domains.forEach((domain) => {
    const domainAttribute = domain ? `; domain=${domain}` : "";
    document.cookie = `${cookieName}=; Max-Age=0; path=/${domainAttribute}; SameSite=Lax`;
  });
};

export const removeTrackingCookies = (): void => {
  if (!isBrowser()) {
    return;
  }

  const trackingCookiePattern =
    /^(?:_ga(?:_|$)|_gid$|_gat|_gcl|li_fat_id|li_giant|AnalyticsSyncHistory|UserMatchHistory)/i;

  document.cookie.split(";").forEach((cookie) => {
    const cookieName = cookie.split("=")[0].trim();
    if (trackingCookiePattern.test(cookieName)) {
      expireCookie(cookieName);
    }
  });
};

export const requiresReloadAfterConsentChange = (
  previousConsent: CookieConsentRecord | null,
  nextConsent: CookieConsentSelection
): boolean =>
  Boolean(
    previousConsent &&
      ((previousConsent.analytics && !nextConsent.analytics) ||
        (previousConsent.marketing && !nextConsent.marketing) ||
        (previousConsent.externalMedia && !nextConsent.externalMedia))
  );
